<template>
  <div class="overlay" @click="$emit('close')">
    <div class="pm-dialog" @click.stop>
      <div class="pm-head">
        <h3 class="pm-title">Nuevo plan de acción</h3>
        <button class="icon-btn" @click="$emit('close')">
          <PortalIcon name="X" :size="20" />
        </button>
      </div>

      <div class="form">
        <!-- El hallazgo de origen. Si el modal se abrió desde una incidencia,
             viene fijo y no se puede cambiar. -->
        <label class="field">
          <span class="field-label">Hallazgo de origen</span>
          <div v-if="claimFijo" class="claim-fijo">
            <strong>{{ claimFijo.folio }}</strong> · {{ claimFijo.cliente }}
            <div class="claim-asunto">{{ claimFijo.asunto }}</div>
          </div>
          <select v-else v-model="f.idClaim" class="inp">
            <option value="">— Selecciona el hallazgo —</option>
            <option v-for="c in claimsDisponibles" :key="c.idClaim" :value="c.idClaim">
              {{ c.folio }} · {{ c.cliente }} — {{ recorta(c.asunto) }}
            </option>
          </select>
          <span v-if="errores.idClaim" class="field-error">{{ errores.idClaim }}</span>
        </label>

        <label class="field">
          <span class="field-label">Título del plan</span>
          <input
            v-model="f.titulo"
            class="inp"
            placeholder="Qué se va a hacer para resolverlo"
          />
          <span v-if="errores.titulo" class="field-error">{{ errores.titulo }}</span>
        </label>

        <label class="field">
          <span class="field-label">Descripción</span>
          <textarea
            v-model="f.descripcion"
            rows="2"
            class="inp textarea"
            placeholder="Alcance del plan (opcional)"
          ></textarea>
        </label>

        <div class="grid-2">
          <label class="field">
            <span class="field-label">Área responsable</span>
            <select v-model="f.idArea" class="inp" :disabled="catLoading">
              <option value="">— Selecciona —</option>
              <option v-for="a in cat.areas" :key="a.idArea" :value="a.idArea">
                {{ a.descripcion }}
              </option>
            </select>
            <span v-if="errores.idArea" class="field-error">{{ errores.idArea }}</span>
            <span v-if="areaSel && areaSel.gerente" class="field-hint">
              Responsable: <strong>{{ areaSel.gerente }}</strong>, gerente del área.
            </span>
            <span v-else-if="areaSel" class="field-hint aviso">
              Esta área aún no tiene gerente capturado. El plan se creará y
              quedará esperando hasta que se registre.
            </span>
            <span v-else class="field-hint">
              El plan se asigna al área; su gerente queda como responsable.
            </span>
          </label>

          <!-- La prioridad no se captura: es la de la incidencia. Tenerla dos
               veces solo permitiría que difirieran. Se muestra para que quien
               crea el plan sepa con qué prioridad va a nacer. -->
          <div class="field">
            <span class="field-label">Prioridad</span>
            <div class="heredado">
              <template v-if="prioridadClaim">
                <strong>{{ prioridadClaim }}</strong>
                <span class="heredado-origen">heredada de la incidencia</span>
              </template>
              <template v-else-if="claimElegido">
                <span class="heredado-vacio">La incidencia no tiene prioridad capturada</span>
              </template>
              <template v-else>
                <span class="heredado-vacio">Elige la incidencia de origen</span>
              </template>
            </div>
          </div>
        </div>

        <label class="field">
          <span class="field-label">Fecha compromiso</span>
          <input v-model="f.fechaCompromiso" type="date" class="inp" />
          <span v-if="errores.fechaCompromiso" class="field-error">
            {{ errores.fechaCompromiso }}
          </span>
          <!-- RN-05: vale la pena que quien asigna sepa que esta fecha se guarda. -->
          <span class="field-hint">
            Se conserva como fecha original. Moverla después requiere aprobación
            del gerente.
          </span>
        </label>

        <div class="grid-2">
          <label class="field">
            <span class="field-label">Resultado esperado</span>
            <textarea
              v-model="f.resultadoEsperado"
              rows="2"
              class="inp textarea"
              placeholder="Opcional"
            ></textarea>
          </label>
          <label class="field">
            <span class="field-label">Criterios de aceptación</span>
            <textarea
              v-model="f.criteriosAceptacion"
              rows="2"
              class="inp textarea"
              placeholder="Opcional"
            ></textarea>
          </label>
        </div>

        <p v-if="catError" class="form-error">{{ catError }}</p>
        <p v-if="error" class="form-error">{{ error }}</p>

        <button
          class="submit"
          :disabled="busy"
          :style="{ background: busy ? '#cbd5e1' : '#003ca5' }"
          @click="crear"
        >
          {{ busy ? "Creando…" : "Crear y asignar al área" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import PortalIcon from "./PortalIcon.vue";
import { fetchCatalogos } from "../../../services/cx.service";
import { validaPlan } from "./cx.validators";

export default {
  name: "NuevoPlanModal",
  components: { PortalIcon },
  props: {
    // Cuando se abre desde una incidencia, el hallazgo viene resuelto.
    claim: { type: Object, default: null },
    // Hallazgos entre los que elegir cuando se abre desde la pestaña Planes.
    claims: { type: Array, default: () => [] },
    busy: { type: Boolean, default: false },
    error: { type: String, default: "" },
  },
  emits: ["close", "crear"],
  data() {
    return {
      cat: { areas: [], prioridades: [], colaboradores: [], estatus: [] },
      catLoading: false,
      catError: "",
      errores: {},
      // Área del hallazgo (texto libre): se usa para preseleccionar la del
      // catálogo cuando los catálogos terminan de cargar.
      preseleccionArea: "",
      f: {
        idClaim: "",
        titulo: "",
        descripcion: "",
        idArea: "",
        fechaCompromiso: "",
        resultadoEsperado: "",
        criteriosAceptacion: "",
      },
    };
  },
  computed: {
    claimFijo() {
      return this.claim;
    },
    // La incidencia de la que va a colgar el plan: la fija, o la elegida.
    claimElegido() {
      if (this.claim) return this.claim;
      if (this.f.idClaim === "" || this.f.idClaim == null) return null;
      return (
        this.claimsDisponibles.find(
          (c) => String(c.idClaim) === String(this.f.idClaim)
        ) || null
      );
    },
    prioridadClaim() {
      return (this.claimElegido && this.claimElegido.prioridad) || "";
    },
    // El área elegida, para mostrar de quién será el plan antes de crearlo.
    areaSel() {
      if (this.f.idArea === "" || this.f.idArea == null) return null;
      return (
        (this.cat.areas || []).find(
          (a) => String(a.idArea) === String(this.f.idArea)
        ) || null
      );
    },
    // Solo se ofrecen hallazgos con id del backend: sin idClaim no hay FK.
    claimsDisponibles() {
      return (this.claims || []).filter((c) => c.idClaim != null);
    },
  },
  async mounted() {
    if (this.claim) {
      this.f.idClaim = this.claim.idClaim;
      // El título arranca con el asunto del hallazgo: casi siempre es el punto
      // de partida y ahorra reescribirlo.
      this.f.titulo = this.recorta(this.claim.asunto, 120);
      if (this.claim.area) this.preseleccionArea = this.claim.area;
    }
    await this.cargarCatalogos();
  },
  methods: {
    recorta(t, n) {
      const s = String(t || "");
      const max = n || 60;
      return s.length > max ? s.slice(0, max) + "…" : s;
    },
    async cargarCatalogos() {
      this.catLoading = true;
      this.catError = "";
      try {
        this.cat = await fetchCatalogos();
        // Si el hallazgo trae área, se preselecciona la que empate por nombre.
        if (this.preseleccionArea) {
          const a = this.cat.areas.find(
            (x) =>
              String(x.descripcion).toLowerCase() ===
              String(this.preseleccionArea).toLowerCase()
          );
          if (a) this.f.idArea = a.idArea;
        }
      } catch (e) {
        this.catError =
          "No se pudieron cargar los catálogos. Revisa la conexión con el servicio.";
      } finally {
        this.catLoading = false;
      }
    },
    crear() {
      if (this.busy) return;
      const datos = { ...this.f, idClaim: this.claim ? this.claim.idClaim : this.f.idClaim };
      const r = validaPlan(datos);
      this.errores = r.errores;
      if (!r.ok) return;
      this.$emit("crear", datos);
    },
  },
};
</script>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.45);
}
.pm-dialog {
  width: 100%;
  max-width: 560px;
  max-height: 100%;
  overflow-y: auto;
  background: var(--pt-card);
  color: var(--pt-text);
  border-radius: 16px;
  padding: 20px;
}
.pm-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.pm-title {
  font-size: 18px;
  font-weight: 700;
  margin: 0;
}
.icon-btn {
  background: none;
  border: 0;
  cursor: pointer;
  color: var(--pt-muted);
}
.form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.field {
  display: block;
}
.field-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--pt-dim);
  margin-bottom: 4px;
}
.field-hint {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: var(--pt-muted);
}
.claim-fijo {
  padding: 8px 12px;
  border-radius: 8px;
  background: var(--pt-elev);
  font-size: 13px;
}
.claim-asunto {
  font-size: 12px;
  color: var(--pt-dim);
  margin-top: 2px;
}
.inp {
  width: 100%;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 14px;
  border: 1px solid var(--pt-border);
  outline: none;
  background: var(--pt-card);
  color: var(--pt-text);
  box-sizing: border-box;
  font-family: inherit;
}
.inp:focus {
  border-color: #003ca5;
}
.textarea {
  resize: none;
}
.form-error {
  margin: 0;
  font-size: 13px;
  color: #b91c1c;
}
.field-error {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #b91c1c;
}
.submit {
  width: 100%;
  padding: 10px;
  border: 0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  margin-top: 4px;
}
.submit:disabled {
  cursor: not-allowed;
}
.field-hint.aviso {
  color: #b45309;
}
.heredado {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  background: var(--pt-elev);
  font-size: 14px;
  min-height: 37px;
  box-sizing: border-box;
}
.heredado-origen {
  font-size: 11px;
  color: var(--pt-muted);
}
.heredado-vacio {
  font-size: 13px;
  color: var(--pt-muted);
  font-style: italic;
}
</style>
